/* Roues — ruling 04/09 (ratified by the owner the same day; amends D-3b).
   ONE WORD: « Roue ». It is the merchant-facing name of the Reward Pool
   (Bible §35): a NAMED SET of gift types with their weights and caps, scoped
   to an establishment or the whole network (0006 wheels.scope). Gift types
   stay a flat library (RW-01); a type may sit on several roues — the N-N
   wheel_rewards link at the v3 freeze. A campaign ALLOCATES one roue (CA-04b)
   and freezes wheel_id at publication (0011). « pool », « lot », « sélection »
   are retired from the merchant vocabulary.

   The LOOK of the wheel — theme, colours, part count, logo — is brand, not
   campaign: it lives in Identité visuelle (R4) and every roue wears it.
   This mock persists roues per world in localStorage; the style in one key. With an API (the ids row, 2b-1) the roues and
   the gift types are the server's, read into lib/mirror: the getters below return them, and nothing is written here. 2b-2:
   with an API a setter changes the mirror and says so (edited), and lib/writes sends it to the server; so does the look,
   which is the workspace's (brand.wheelDesign). */
import { currentWorld, type World } from '@/mocks/worlds'
import { apiWrites } from './api'
import { readBrandIdentity } from './brand'
import { edited, homeLocationId, mirror, serverIdOf, setMirror } from './mirror'
import { setWorkspace } from './scope'

/* ======================= STYLE (Identité visuelle) ======================= */

export interface WheelDesign {
  preset: 'classique' | 'epure' | 'genereux'
  parts: number  // D-1 (02/09): 4 / 6 / 8 — F-15 look, never a map of the odds
  a: string      // principal part colour
  b: string      // alternate part colour
  logo: boolean  // merchant logo at the centre
  logoUrl?: string | null  // the uploaded logo (POST /v1/app/media → brand.logoUrl); absent = the placeholder letter
  /* v3.3 WF-038 (parametres vet ruling 3): the brand's welcome sentence and salon photo — a new campaign's « entry.h »
     starts from the sentence (lib/campaign textFor); this browser's store (lib/brand) until a server field exists */
  welcome?: string | null
  photoUrl?: string | null
}

export const DEFAULT_DESIGN: WheelDesign = {
  preset: 'classique', parts: 6, a: '#237A55', b: '#F4F1E8', logo: true,
}

/* D-2 (02/09) — preset themes are curated palette-pair bundles inside §53's
   rules: contrast is still validated before publish, and spark/gold is never
   a background here (reserved for prize moments, F-10). */
export interface WheelTheme { id: string; name: string; a: string; b: string; note: string }
export const THEMES: WheelTheme[] = [
  { id: 'minimal', name: 'Minimal', a: '#20281F', b: '#F4F1E8', note: 'encre & papier — sobre, universel' },
  { id: 'beaute', name: 'Beauté', a: '#8E5A6E', b: '#F5EAF0', note: 'prune douce — instituts & salons' },
  { id: 'restaurant', name: 'Restaurant', a: '#9C3B2E', b: '#F6E9DC', note: 'terracotta chaud — tables & cafés' },
  { id: 'fetes', name: 'Fêtes', a: '#1B5E42', b: '#EED9B0', note: 'sapin & or pâle — saisonnier' },
]

const DESIGN_KEY = 'bmr.wheel-design'

/** The one look every roue wears (workspace-level, ST-01 inheritance). With an API, the workspace's (GET /v1/app/workspace). */
export function getWheelDesign(): WheelDesign {
  const brand = readBrandIdentity()
  if (apiWrites) { const d = mirror().workspace?.wheelDesign; return { ...DEFAULT_DESIGN, ...(d ?? {}), ...brand } }
  try {
    const raw = localStorage.getItem(DESIGN_KEY)
    if (raw) {
      const d = JSON.parse(raw) as Partial<WheelDesign>
      if (d && typeof d.parts === 'number' && d.a && d.b) return { ...DEFAULT_DESIGN, ...d, ...brand } as WheelDesign
    }
  } catch { /* private mode / bad JSON */ }
  return { ...DEFAULT_DESIGN, ...brand }
}
export function setWheelDesign(d: WheelDesign): void {
  if (apiWrites) {
    setWorkspace({ wheelDesign: d }); edited({ kind: 'workspace', fields: ['wheelDesign'] }); return
  }
  try { localStorage.setItem(DESIGN_KEY, JSON.stringify(d)) } catch { /* non-persistent is fine */ }
}

/** CSS conic-gradient stops for a design — used by every wheel render. */
export function wheelStops(d: WheelDesign): string {
  const seg = 360 / d.parts
  return Array.from({ length: d.parts }, (_, i) =>
    `${i % 2 === 0 ? d.a : d.b} ${i * seg}deg ${(i + 1) * seg}deg`).join(', ')
}

/** The wheel, rendered — Identité's preview, RW-12's, the builder's. */
export function Wheel({ design, size = 210 }: { design: WheelDesign; size?: number }) {
  const hub = Math.round(size * 0.27)
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }} aria-label="Aperçu de la roue">
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
        background: `conic-gradient(${wheelStops(design)})`, border: '6px solid var(--bmr-ink)' }} />
      <div style={{ position: 'absolute', inset: 0, margin: 'auto', width: hub, height: hub,
        borderRadius: '50%', background: 'var(--bmr-paper)', border: '3px solid var(--bmr-ink)',
        display: 'grid', placeItems: 'center', font: `700 ${Math.round(size * 0.085)}px 'Plus Jakarta Sans', sans-serif` }}>
        {design.logo ? (design.logoUrl ? <img src={design.logoUrl} alt="" style={{ width: '72%', height: '72%', objectFit: 'contain', borderRadius: '50%' }} /> : 'V') : ''}
      </div>
      <div style={{ position: 'absolute', top: -4, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0, borderLeft: '9px solid transparent', borderRight: '9px solid transparent',
        borderTop: '14px solid var(--bmr-ink)' }} />
    </div>
  )
}

/* ============================ ROUES (Cadeaux) ============================ */

/** One gift type ON a roue: the library entry (snapshotted name/cost so an
 *  express-created type can live here too) plus its weight and monthly cap. */
export interface RoueReward {
  rewardId: string
  name: string
  cost: number         // € — private, optional (0 = no cost)
  weight: number       // integer; total is free, the % shown is normalised
  cap: number | null   // monthly stock cap; null = unlimited
  excluded?: string    // pre-draw exclusion reason, visible (RW-03 rule): « stock épuisé ce mois »
}

export type RoueScope = 'location' | 'group'

/* Scope v5 · Révélation — when the invitation shows around a gift already drawn — belongs to the roue (the owner's call, 19/09).
   One roue, one reveal behaviour wherever it plays; another moment → duplicate the roue. */
export type RevealMoment = 'pendant' | 'immediat' | 'apres'   // pendant, avant l’ouverture du cadeau · après le cadeau · plus tard, par e-mail
export interface RoueReveal { moment: RevealMoment; countdown: 0 | 15 | 30 | 45; earlyOpen: boolean; googleAck?: boolean }
export const DEFAULT_REVEAL: RoueReveal = { moment: 'immediat', countdown: 30, earlyOpen: true }
export const REVEAL_TITLES: Record<RevealMoment, string> = { pendant: 'Pendant, avant l’ouverture du cadeau', immediat: 'Après le cadeau', apres: 'Plus tard, par e-mail' }
export interface Roue {
  id: string
  name: string
  /** Scope v5 · section C of the roue: when the invitation shows */
  reveal?: RoueReveal
  /** Scope v5 · section D: the roue's own screens (spin.h, spin.btn, reveal.p, wallet) */
  texts?: Record<string, string>
  scope: RoueScope          // 0006: établissement (default) or réseau
  locations?: string[]      // owner direction 04/09 (D-20): the establishments where this roue plays; empty + 'group' = everywhere
  design?: WheelDesign      // D-20: optional look of its own; absent = Identité visuelle's style (R4 default)
  rewards: RoueReward[]     // 1–12 active types (RW-12 rule)
  usedBy: string[]          // campaigns referencing it (mock — the real one joins campaign_versions)
  createdVia?: 'express'    // R2: express creates a roue named after the campaign — nothing hidden
  pending?: boolean         // D-29 (05/09): edited after a campaign used it — « modifications à publier » until that campaign republishes
}

export const roueReveal = (r?: Roue | null): RoueReveal => ({ ...DEFAULT_REVEAL, ...(r?.reveal ?? {}) })
/** A one-gift roue: revealed without a spin (« cadeau unique »). */
export const isCadeauUnique = (r?: Roue | null) => !!r && r.rewards.filter(x => !x.excluded).length < 2
/** The look a roue wears: its own, else the brand's (Identité visuelle). */
export const designFor = (roue?: Roue | null): WheelDesign => roue?.design ?? getWheelDesign()

/* D-1 (02/09) — the part-count presets, shared by Identité and RW-12 */
export const PARTS = [
  { id: 'epure', name: 'Épuré', parts: 4 },
  { id: 'classique', name: 'Classique', parts: 6 },
  { id: 'genereux', name: 'Généreux', parts: 8 },
] as const

export const ROUE_MIN_PCT = 0.5   // D-5 amendment (02/09): the floor, published like any other odds
export const ROUE_MAX_TYPES = 12

/* ---------- seeds per world (≥ 2 roues where the world has gifts; a réseau roue in
   nine-locations; nothing in day-one so the empty state is met honestly) ---------- */
const r = (rewardId: string, name: string, cost: number, weight: number, cap: number | null, excluded?: string): RoueReward =>
  ({ rewardId, name, cost, weight, cap, ...(excluded ? { excluded } : {}) })

function seeds(w: World): Roue[] {
  if (w === 'day-one') return []
  if (w === 'everything-wrong') return [
    { id: 'w-salon', name: 'Roue du salon', scope: 'location', usedBy: ['Rentrée 2026'], rewards: [
      r('g1', 'Café offert', 2.5, 50, 40, 'stock épuisé ce mois'),
      r('g2', '10 % sur la prochaine prestation', 6.2, 30, null),
      r('g3', 'Soin profond offert', 6.5, 20, 9),
    ] },
  ]
  const salon: Roue = { id: 'w-salon', name: 'Roue du salon', scope: 'location', locations: ['l0'],
    usedBy: w === 'nine-locations' ? ['Rentrée 2026 — Paris 9'] : ['Rentrée 2026'], rewards: [
      r('g1', 'Café offert', 2.5, 40, null),
      r('g2', '10 % sur la prochaine prestation', 6.2, 30, null),
      r('g3', 'Soin profond offert', 6.5, 20, 40),
      r('g4', 'Brushing offert', 15, 7, 10),
      r('g5', 'Coupe offerte', 22, 3, 2),
    ] }
  if (w === 'nine-locations') return [
    salon,
    { id: 'w-noel', name: 'Roue de Noël', scope: 'group', locations: [], design: { preset: 'genereux', parts: 8, a: '#1B5E42', b: '#EED9B0', logo: true }, usedBy: [], rewards: [
      r('g1', 'Café offert', 2.5, 45, null),
      r('g3', 'Soin profond offert', 6.5, 35, 40),
      r('g5', 'Coupe offerte', 22, 20, 2),
    ] },
  ]
  return [
    salon,
    { id: 'w-premium', name: 'Roue Premium', scope: 'location', locations: ['l0'], usedBy: [], rewards: [
      r('g3', 'Soin profond offert', 6.5, 50, 40),
      r('g4', 'Brushing offert', 15, 35, 10),
      r('g5', 'Coupe offerte', 22, 15, 2),
    ] },
  ]
}

const rouesKey = () => `bmr.roues.${currentWorld()}`
const activeKey = () => `bmr.active-roue.${currentWorld()}`

/** The library: with an API the server's (lib/mirror), else the current world's. */
export function getRoues(): Roue[] {
  return apiWrites ? mirror().roues : worldRoues()
}
/** The current world's library — its store, its seeds when nothing was saved yet: the demo's, and what the mock server
    of a build that points at itself starts from (mocks/handlers-b). */
export function worldRoues(): Roue[] {
  try {
    const raw = localStorage.getItem(rouesKey())
    if (raw) {
      const list = JSON.parse(raw) as Roue[]
      if (Array.isArray(list) && list.every(x => x && x.id && Array.isArray(x.rewards))) return list
    }
  } catch { /* private mode / bad JSON */ }
  return seeds(currentWorld())
}
export function setRoues(list: Roue[]): void {
  if (apiWrites) {
    /* a roue or a gift type made in this tab, held by a screen under its first id, lands under the server's (serverIdOf) */
    const before = mirror().roues
    setMirror({ roues: list.map(w => ({ ...w, id: serverIdOf(w.id), rewards: w.rewards.map(x => ({ ...x, rewardId: serverIdOf(x.rewardId) })) })) })
    edited({ kind: 'roues', before }); return
  }
  try { localStorage.setItem(rouesKey(), JSON.stringify(list)) } catch { /* fine */ }
}

/** The roue a new campaign / the express path starts from. */
export function getActiveRoueId(): string | null {
  if (apiWrites) { const { roues, activeRoueId } = mirror(); return activeRoueId && roues.some(x => x.id === activeRoueId) ? activeRoueId : roues[0]?.id ?? null }
  try {
    const id = localStorage.getItem(activeKey())
    const list = getRoues()
    if (id && list.some(x => x.id === id)) return id
    return list[0]?.id ?? null
  } catch { return getRoues()[0]?.id ?? null }
}
export function setActiveRoueId(id: string): void {
  if (apiWrites) { setMirror({ activeRoueId: serverIdOf(id) }); return }
  try { localStorage.setItem(activeKey(), id) } catch { /* fine */ }
}
export function getRoue(id: string | null | undefined): Roue | undefined {
  const list = getRoues()
  return list.find(x => x.id === (apiWrites && id ? serverIdOf(id) : id)) ?? list[0]
}

export const newRoueId = () => `w-${Date.now().toString(36)}`

export function duplicateRoue(src: Roue): Roue {
  return { ...src, id: newRoueId(), name: `${src.name} (copie)`, usedBy: [], createdVia: undefined,
    rewards: src.rewards.map(x => ({ ...x })) }
}

/* ---------- derived numbers (the RW-03 simulator, now inside RW-12) ---------- */

/** Types that take part in the next draw — a capped-out or excluded type is
 *  removed BEFORE the draw and the remaining odds are re-normalised (visible). */
export const drawable = (roue: Roue) => roue.rewards.filter(x => !x.excluded && x.weight > 0)

/** Normalised probability (%) of each drawable type; excluded → 0. */
export function odds(roue: Roue): Record<string, number> {
  const live = drawable(roue)
  const total = live.reduce((s, x) => s + x.weight, 0)
  const out: Record<string, number> = {}
  for (const x of roue.rewards) out[x.rewardId] = live.includes(x) && total > 0 ? (x.weight / total) * 100 : 0
  return out
}

/** Expected cost per participation at full use (€). */
export function expectedCost(roue: Roue): number {
  const o = odds(roue)
  return roue.rewards.reduce((s, x) => s + (o[x.rewardId] / 100) * x.cost, 0)
}

/** Publishability — the rules RW-12 keeps: 1–12 types, no zero weight, the 0.5 % floor. */
export function roueIssues(roue: Roue): string[] {
  const issues: string[] = []
  if (!roue.name.trim()) issues.push('Donnez un nom à la roue.')
  if (roue.rewards.length === 0) issues.push('Ajoutez au moins un type de cadeau.')
  if (roue.rewards.length > ROUE_MAX_TYPES) issues.push(`Au plus ${ROUE_MAX_TYPES} types sur une roue.`)
  if (roue.rewards.some(x => x.weight <= 0)) issues.push('Un poids nul n’est pas publiable — chaque part visible reste gagnable.')
  const o = odds(roue)
  const low = drawable(roue).filter(x => o[x.rewardId] < ROUE_MIN_PCT)
  if (low.length) issues.push(`Sous le plancher de ${ROUE_MIN_PCT.toLocaleString('fr-FR')} % : ${low.map(x => x.name).join(', ')}.`)
  return issues
}

export const fmtEur = (n: number) => `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
export const fmtPct = (n: number) => `${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
export const scopeLabel = (s: RoueScope) => s === 'group' ? 'Tous les établissements' : 'Établissement'

/* ---------- D-33 (05/09) · the editor speaks in percent; the seam keeps integer weights.
   In the editor a roue is carried in TENTHS of a percent (weights sum to 1 000 = 100 %);
   `odds()` normalises, so nothing downstream changes. ---------- */
export const TENTHS = 1000
/** Convert any roue to tenths — the sum becomes exactly 1 000 (the largest row absorbs rounding). */
export function toTenths(roue: Roue): Roue {
  const o = odds(roue)
  const live = roue.rewards.filter(x => !x.excluded)
  let rewards = roue.rewards.map(x => ({ ...x, weight: x.excluded ? x.weight : Math.round((o[x.rewardId] || 0) * 10) }))
  const sum = rewards.filter(x => !x.excluded).reduce((s, x) => s + x.weight, 0)
  if (live.length && sum !== TENTHS) {
    const big = rewards.filter(x => !x.excluded).reduce((a, b) => (b.weight > a.weight ? b : a))
    rewards = rewards.map(x => x.rewardId === big.rewardId ? { ...x, weight: x.weight + (TENTHS - sum) } : x)
  }
  return { ...roue, rewards }
}
export const tenthsTotal = (roue: Roue) => roue.rewards.filter(x => !x.excluded).reduce((s, x) => s + x.weight, 0)
/** The gap to 100 % in percent (positive = missing, negative = too much). */
export const pctGap = (roue: Roue) => (TENTHS - tenthsTotal(roue)) / 10
/** Set one row's share (percent) — the others keep theirs; the total line says the rest. */
export function setSharePct(roue: Roue, rewardId: string, pct: number): Roue {
  const w = Math.max(0, Math.round(pct * 10))
  return { ...roue, rewards: roue.rewards.map(x => x.rewardId === rewardId ? { ...x, weight: w } : x) }
}
/** « Compléter » — the gap goes to the last unlocked row (missing) or is taken proportionally from the unlocked rows (too much). */
export function completeShares(roue: Roue, locked: Set<string>): Roue {
  const gap = TENTHS - tenthsTotal(roue)
  if (gap === 0) return roue
  const free = roue.rewards.filter(x => !x.excluded && !locked.has(x.rewardId))
  if (!free.length) return roue
  if (gap > 0) { const last = free[free.length - 1]; return { ...roue, rewards: roue.rewards.map(x => x.rewardId === last.rewardId ? { ...x, weight: x.weight + gap } : x) } }
  const freeSum = free.reduce((s, x) => s + x.weight, 0) || 1
  let taken = 0
  const rewards = roue.rewards.map(x => {
    if (x.excluded || locked.has(x.rewardId)) return x
    const cut = Math.min(x.weight, Math.round((-gap) * (x.weight / freeSum))); taken += cut
    return { ...x, weight: x.weight - cut }
  })
  const rest = -gap - taken
  if (rest !== 0) { const big = rewards.filter(x => !x.excluded && !locked.has(x.rewardId)).reduce((a, b) => (b.weight > a.weight ? b : a)); return { ...roue, rewards: rewards.map(x => x.rewardId === big.rewardId ? { ...x, weight: Math.max(0, x.weight - rest) } : x) } }
  return { ...roue, rewards }
}
/** « Équilibrer » — equal shares of what the locked rows leave. */
export function balanceShares(roue: Roue, locked: Set<string>): Roue {
  const free = roue.rewards.filter(x => !x.excluded && !locked.has(x.rewardId))
  if (!free.length) return roue
  const lockedSum = roue.rewards.filter(x => !x.excluded && locked.has(x.rewardId)).reduce((s, x) => s + x.weight, 0)
  const room = Math.max(0, TENTHS - lockedSum)
  const each = Math.floor(room / free.length)
  let rest = room - each * free.length
  return { ...roue, rewards: roue.rewards.map(x => {
    if (x.excluded || locked.has(x.rewardId)) return x
    const extra = rest > 0 ? 1 : 0; rest -= extra
    return { ...x, weight: each + extra }
  }) }
}
/** « Répartition recommandée » — from cost: the cheapest carries the most, an expensive type never above 10 % unless locked; every type ≥ the floor. */
export function recommendShares(roue: Roue, locked: Set<string>): Roue {
  const free = roue.rewards.filter(x => !x.excluded && !locked.has(x.rewardId))
  if (!free.length) return roue
  const lockedSum = roue.rewards.filter(x => !x.excluded && locked.has(x.rewardId)).reduce((s, x) => s + x.weight, 0)
  const room = Math.max(0, TENTHS - lockedSum)
  const costs = free.map(x => Math.max(0.5, x.cost || 1))
  const maxCost = Math.max(...costs)
  const raw = costs.map(c => 1 / c)
  const rawSum = raw.reduce((s, v) => s + v, 0)
  let shares = raw.map(v => (v / rawSum) * room)
  /* cap: anything costing ≥ 60 % of the dearest type stays ≤ 10 % of the roue */
  const cap = TENTHS * 0.10
  let excess = 0
  shares = shares.map((s, i) => { if (costs[i] >= maxCost * 0.6 && free.length > 1 && s > cap) { excess += s - cap; return cap } return s })
  if (excess > 0) { const openIdx = shares.map((_, i) => i).filter(i => !(costs[i] >= maxCost * 0.6 && free.length > 1)); const openSum = openIdx.reduce((s, i) => s + shares[i], 0) || 1; openIdx.forEach(i => { shares[i] += excess * (shares[i] / openSum) }) }
  const floor = ROUE_MIN_PCT * 10
  const rounded = shares.map(s => Math.max(floor, Math.round(s)))
  const diff = room - rounded.reduce((s, v) => s + v, 0)
  const bigI = rounded.indexOf(Math.max(...rounded)); rounded[bigI] = Math.max(floor, rounded[bigI] + diff)
  const byId = new Map(free.map((x, i) => [x.rewardId, rounded[i]]))
  return { ...roue, rewards: roue.rewards.map(x => byId.has(x.rewardId) ? { ...x, weight: byId.get(x.rewardId)! } : x) }
}
/** « Sur 100 participations : 45 cafés · 20 soins… » — the merchant's sentence for a roue's odds. Whole numbers by
 *  largest remainder (lib-pack-8), so the sentence sums to the 100 it promises even when shares are in tenths. */
export function per100(roue: Roue): string {
  const o = odds(roue)
  const shown = roue.rewards.filter(x => !x.excluded)
  const n = shown.map(x => Math.floor(o[x.rewardId] + 1e-9))
  let left = Math.round(shown.reduce((s, x) => s + o[x.rewardId], 0)) - n.reduce((s, v) => s + v, 0)
  const byRest = shown.map((_, i) => i).sort((a, b) => (o[shown[b].rewardId] - n[b]) - (o[shown[a].rewardId] - n[a]))
  for (const i of byRest) { if (left <= 0) break; n[i]++; left-- }
  return shown.map((x, i) => `${n[i]} × ${x.name.toLowerCase()}`).join(' · ')
}
/** Where a roue's gifts can be used — D-39 (05/09): where the roue plays. */
export const redeemLabel = (roue: Roue, names: Record<string, string> = {}) => placesLabel(roue, names)

/* ============================ GIFT TYPES (RW-01/02) ============================ */

/** A gift type as the merchant edits it. The API mock owns the seeds; the
 *  merchant's own creations/edits live in a per-world overlay so day-one is
 *  real: create a type → put it on a roue → allocate → publish. */
export interface GiftType {
  id: string; name: string; internal: string; cost: number; validityDays: number
  /** D-44 (05/09): the monthly cap lives on the roue row; a type carries none — kept for the fixtures' seeds only */
  cap: number | null; condition: string; status: 'ok' | 'low' | 'out'; stock: number | null
  icon?: string; category?: GiftCategory; sameDay?: boolean; links?: string
  /** lib-pack-17 (bibliotheques vet ruling 2) · an archived type leaves the library's lists and the roue editor's picker; the
      gifts already issued keep their words, a roue that still carries it keeps it until the merchant takes it off */
  archived?: boolean
}
/** The types a merchant can still pick — the library without its archived ones. */
export const activeTypes = (list: GiftType[]): GiftType[] => list.filter(t => !t.archived)

/** D-84 · words that tie a gift to a review — the gift's name and its condition never use them (the API checks the same list) */
export const REVIEW_WORDS = /(?<!\p{L})(avis|reviews?|[ée]toiles?|[ée]valu(?:ations?|ez|er)|not(?:ez|er|ations?)|rating|preuves?|proof|capture d['’ ]?[ée]cran|screenshot|commentaires?|google|tripadvisor|trustpilot|bonnes? notes?)(?!\p{L})|★|(?<!\d)\d\s*\/\s*5(?!\d)/iu
export const speaksOfReview = (text: string) => REVIEW_WORDS.test(text)

/* ---------- D-35 (05/09) · the icon library by category — a prototype set in the
   brand's stroke; the duotone sprite in packages/brand replaces it at M2 ---------- */
export type GiftCategory = 'boissons' | 'restauration' | 'beaute' | 'remises' | 'produits' | 'experiences' | 'general'
export const GIFT_CATEGORIES: { id: GiftCategory; label: string }[] = [
  { id: 'boissons', label: 'Boissons' }, { id: 'restauration', label: 'Restauration' }, { id: 'beaute', label: 'Beauté & soins' },
  { id: 'remises', label: 'Remises' }, { id: 'produits', label: 'Produits' }, { id: 'experiences', label: 'Expériences' }, { id: 'general', label: 'Général' },
]
export const GIFT_ICONS: { id: string; cat: GiftCategory; label: string; d: string }[] = [
  { id: 'cafe', cat: 'boissons', label: 'Café', d: 'M5 8h11v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V8ZM16 10h2a2 2 0 0 1 0 4h-2M4 21h14M8 3c0 1.5 1 1.5 1 3M11 3c0 1.5 1 1.5 1 3' },
  { id: 'the', cat: 'boissons', label: 'Thé', d: 'M6 9h11v5a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5V9ZM17 11h2a2 2 0 0 1 0 4h-2M12 9V5l3-2M5 21h13' },
  { id: 'cocktail', cat: 'boissons', label: 'Cocktail', d: 'M4 4h16l-8 9-8-9ZM12 13v7M8 20h8' },
  { id: 'jus', cat: 'boissons', label: 'Jus', d: 'M7 4h10l-1 16H8L7 4ZM7 10h10M14 4l3-2' },
  { id: 'croissant', cat: 'restauration', label: 'Viennoiserie', d: 'M4 14c2-6 6-9 12-9 2 0 3 1 3 3 0 5-4 9-9 11-2 1-4 0-4-2 0-1 1-2 2-2M4 14c-1 2 0 4 2 4 1 0 2-1 2-2' },
  { id: 'dessert', cat: 'restauration', label: 'Dessert', d: 'M6 10h12l-1 9H7l-1-9ZM5 10a7 5 0 0 1 14 0M12 5V3' },
  { id: 'pizza', cat: 'restauration', label: 'Pizza', d: 'M12 3l9 16H3l9-16ZM10 13h.01M13 10h.01M12 16h.01' },
  { id: 'plat', cat: 'restauration', label: 'Plat', d: 'M4 15h16M5 15a7 7 0 0 1 14 0M12 8V6M3 19h18' },
  { id: 'ciseaux', cat: 'beaute', label: 'Coupe', d: 'M6 7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM6 12a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM8 11l12-7M8 13l12 7' },
  { id: 'brosse', cat: 'beaute', label: 'Brushing', d: 'M4 20l4-4M8 16l8-8M12 4l8 8-4 4-8-8 4-4ZM14 6l2 2M12 8l2 2M10 10l2 2' },
  { id: 'vernis', cat: 'beaute', label: 'Vernis', d: 'M9 3h6v4H9zM8 7h8v3H8zM7 10h10v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9Z' },
  { id: 'creme', cat: 'beaute', label: 'Soin', d: 'M5 9h14v9a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V9ZM7 9V6h10v3' },
  { id: 'pourcent', cat: 'remises', label: 'Pourcentage', d: 'M6 18L18 6M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM16 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z' },
  { id: 'euro', cat: 'remises', label: 'Euros', d: 'M17 6a7 7 0 0 0-10 3M17 18a7 7 0 0 1-10-3M4 10h9M4 14h9' },
  { id: 'etiquette', cat: 'remises', label: 'Prix', d: 'M3 12V4h8l10 10-8 8L3 12ZM7 8h.01' },
  { id: 'bon', cat: 'remises', label: 'Bon', d: 'M4 8h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4V8ZM10 8v10' },
  { id: 'sac', cat: 'produits', label: 'Produit', d: 'M5 8h14l1 12H4L5 8ZM9 8V6a3 3 0 0 1 6 0v2' },
  { id: 'flacon', cat: 'produits', label: 'Flacon', d: 'M9 3h6M10 3v4l-3 5v7a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-7l-3-5V3' },
  { id: 'bougie', cat: 'produits', label: 'Bougie', d: 'M9 9h6v12H9zM12 3c-1.5 2-2 3-2 4a2 2 0 0 0 4 0c0-1-.5-2-2-4Z' },
  { id: 'livre', cat: 'produits', label: 'Livre', d: 'M4 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4V4ZM20 4h-6a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h7V4Z' },
  { id: 'etoile', cat: 'experiences', label: 'Privilège', d: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3Z' },
  { id: 'calendrier', cat: 'experiences', label: 'Séance', d: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4' },
  { id: 'coeur', cat: 'experiences', label: 'Attention', d: 'M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z' },
  { id: 'musique', cat: 'experiences', label: 'Sortie', d: 'M9 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM19 16a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM9 18V6l10-2v12' },
  { id: 'cadeau', cat: 'general', label: 'Cadeau', d: 'M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-2-4-6-3-6-1s4 1 6 1ZM12 7c2-4 6-3 6-1s-4 1-6 1Z' },
  { id: 'etincelle', cat: 'general', label: 'Surprise', d: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM19 17l.7 1.8 1.8.7-1.8.7L19 22l-.7-1.8-1.8-.7 1.8-.7.7-1.8Z' },
  { id: 'main', cat: 'general', label: 'Geste', d: 'M7 11V6a1.5 1.5 0 0 1 3 0v5M10 10V4a1.5 1.5 0 0 1 3 0v6M13 10V5a1.5 1.5 0 0 1 3 0v6M16 11a1.5 1.5 0 0 1 3 0v4a6 6 0 0 1-6 6h-2a6 6 0 0 1-6-6v-3' },
  { id: 'trophee', cat: 'general', label: 'Récompense', d: 'M8 4h8v5a4 4 0 0 1-8 0V4ZM8 6H5a3 3 0 0 0 3 3M16 6h3a3 3 0 0 1-3 3M12 13v4M9 20h6M10 17h4v3h-4z' },
]
export const giftIcon = (id?: string) => GIFT_ICONS.find(i => i.id === id) ?? GIFT_ICONS.find(i => i.id === 'cadeau')!
/** A best guess from the name — the merchant confirms or changes it. */
export function guessIcon(name: string): string {
  const n = name.toLowerCase()
  const rules: [RegExp, string][] = [[/caf/, 'cafe'], [/thé|the\b|infusion/, 'the'], [/cocktail|verre|vin|bière|biere/, 'cocktail'], [/jus|smoothie|boisson/, 'jus'],
    [/croissant|viennois|pain/, 'croissant'], [/dessert|gâteau|gateau|pâtiss|patiss/, 'dessert'], [/pizza/, 'pizza'], [/plat|menu|déjeuner|dejeuner|dîner|diner/, 'plat'],
    [/coupe|coiff/, 'ciseaux'], [/brushing/, 'brosse'], [/vernis|ongle|manucure/, 'vernis'], [/soin|massage|crème|creme/, 'creme'],
    [/%|pour ?cent|remise|réduc|reduc/, 'pourcent'], [/€|euro/, 'euro'], [/prix|étiquette/, 'etiquette'], [/bon\b|avoir/, 'bon'],
    [/produit|sac|article/, 'sac'], [/flacon|parfum|shampo/, 'flacon'], [/bougie/, 'bougie'], [/livre/, 'livre'],
    [/vip|privil|accès|acces/, 'etoile'], [/séance|seance|rendez|rdv|atelier/, 'calendrier'], [/attention|surprise/, 'coeur'], [/sortie|concert|place/, 'musique']]
  for (const [re, id] of rules) if (re.test(n)) return id
  return 'cadeau'
}
const typesKey = () => `bmr.types.${currentWorld()}`
export function getLocalTypes(): GiftType[] {
  return apiWrites ? mirror().types : storedTypes()
}
function storedTypes(): GiftType[] {
  try { const raw = localStorage.getItem(typesKey()); if (raw) { const l = JSON.parse(raw); if (Array.isArray(l)) return l } } catch { /* fine */ }
  return []
}
export function setLocalTypes(list: GiftType[]): void {
  if (apiWrites) { setMirror({ types: list }); return }
  try { localStorage.setItem(typesKey(), JSON.stringify(list)) } catch { /* fine */ }
}
/** API seeds + the overlay (overlay wins on the same id). With an API, the server's library (GET /v1/app/gift-types). */
export function mergeTypes(api: { id: string; name: string; cost: string; stock: number | null; status: 'ok' | 'low' | 'out' }[]): GiftType[] {
  return apiWrites ? mirror().types : worldTypes(api)
}
/** The current world's library: the seeds the summary carries, and the merchant's own over them (the demo's mergeTypes). */
export function worldTypes(api: { id: string; name: string; cost: string; stock: number | null; status: 'ok' | 'low' | 'out' }[]): GiftType[] {
  const local = storedTypes()
  const fromApi: GiftType[] = api.map(t => ({
    id: t.id, name: t.name, internal: t.name, cost: parseFloat(t.cost.replace(',', '.').replace(/[^\d.]/g, '')) || 0,
    validityDays: 60, cap: t.stock, condition: '', status: t.status, stock: t.stock,
  }))
  const byId = new Map<string, GiftType>()
  for (const t of fromApi) byId.set(t.id, t)
  for (const t of local) byId.set(t.id, { ...(byId.get(t.id) ?? t), ...t })
  return [...byId.values()]
}
export const newTypeId = () => `t-${Date.now().toString(36)}`
export function upsertType(t0: GiftType): void {
  const t = apiWrites ? { ...t0, id: serverIdOf(t0.id) } : t0
  const local = getLocalTypes()
  const was = local.find(x => x.id === t.id)
  setLocalTypes(was ? local.map(x => x.id === t.id ? t : x) : [...local, t])
  edited({ kind: 'type', id: t.id })
  /* a renamed / re-costed type updates its snapshot on every roue it sits on. A campaign's published version froze
     each gift's label, condition, validity and cost (the API's `frozenRoue`), so on a roue a campaign uses the change
     waits for that campaign to republish: « modifications à publier » (D-29, lib-pack-8). A stock-out is live (RW-03),
     never a change to publish. A seed never edited reads as the API mock gives it: no condition, 60 days. */
  const before = { condition: was?.condition ?? '', validityDays: was?.validityDays || 60, sameDay: !!was?.sameDay, icon: was?.icon }
  const changes = (x: RoueReward) => x.name !== t.name || x.cost !== t.cost || before.condition !== (t.condition ?? '')
    || before.validityDays !== (t.validityDays || 60) || before.sameDay !== !!t.sameDay || (!!before.icon && !!t.icon && before.icon !== t.icon)
  setRoues(getRoues().map(w => {
    const on = w.rewards.find(x => x.rewardId === t.id)
    const told = !!on && !w.pending && w.usedBy.length > 0 && changes(on)
    return { ...w, ...(told ? { pending: true } : {}), rewards: w.rewards.map(x => x.rewardId === t.id
      ? { ...x, name: t.name, cost: t.cost, cap: x.cap ?? t.cap, ...(t.status === 'out' ? { excluded: 'stock épuisé ce mois' } : { excluded: undefined }) } : x) }
  }))
}
/** « Archiver » a gift type (lib-pack-17): it stays in the store, flagged — never deleted, its issued gifts and its figures
    stand. A type a roue still carries is refused with the roues' names, so the merchant takes it off them first. */
export function archiveType(typeId: string): { refused?: string } {
  const id = apiWrites ? serverIdOf(typeId) : typeId
  const on = rouesUsingType(id)
  if (on.length) return { refused: `Ce cadeau est encore sur ${on.length > 1 ? 'les roues' : 'la roue'} ${on.map(w => `« ${w.name} »`).join(', ')} — retirez-le d’abord.` }
  const local = getLocalTypes()
  const was = local.find(x => x.id === id)
  const t: GiftType = was ? { ...was, archived: true } : { id, name: '', internal: '', cost: 0, validityDays: 60, cap: null, condition: '', status: 'ok', stock: null, archived: true }
  setLocalTypes(was ? local.map(x => x.id === id ? t : x) : [...local, t])
  edited({ kind: 'type', id })
  return {}
}
/** « Retirer » a roue (WF-072, lib-pack-17): gone from the library; a roue a campaign uses is refused with the campaigns'
    names — the campaign chooses another roue first (D-29: a published version keeps its frozen copy whatever happens here). */
export function removeRoue(roueId: string): { refused?: string } {
  const id = apiWrites ? serverIdOf(roueId) : roueId
  const w = getRoues().find(x => x.id === id)
  if (!w) return {}
  if (w.usedBy.length) return { refused: `Cette roue est choisie par ${w.usedBy.map(c => `« ${c} »`).join(', ')} — choisissez-leur une autre roue d’abord.` }
  setRoues(getRoues().filter(x => x.id !== id))
  if (getActiveRoueId() === id) { const next = getRoues()[0]; if (next) setActiveRoueId(next.id) }
  return {}
}
/** Which roues carry this type — the prize → roue link (RW-01 meta, RW-02 checkboxes). */
export const rouesUsingType = (typeId: string): Roue[] => { const id = apiWrites ? serverIdOf(typeId) : typeId; return getRoues().filter(w => w.rewards.some(x => x.rewardId === id)) }
export function toReward(t: GiftType, weight = 10): RoueReward {
  return { rewardId: t.id, name: t.name, cost: t.cost, weight, cap: t.cap, ...(t.status === 'out' ? { excluded: 'stock épuisé ce mois' } : {}) }
}
export function setTypeOnRoues(t0: GiftType, roueIds0: string[]): void {
  /* 2b-2 · ids a screen kept from before the server answered still find their gift type and their roues */
  const t = apiWrites ? { ...t0, id: serverIdOf(t0.id) } : t0
  const roueIds = apiWrites ? roueIds0.map(serverIdOf) : roueIds0
  setRoues(getRoues().map(w => {
    const on = w.rewards.some(x => x.rewardId === t.id); const want = roueIds.includes(w.id)
    /* a gift on or off a roue a campaign uses waits for that campaign to republish (D-29) */
    const told = on !== want && w.usedBy.length > 0 ? { pending: true } : {}
    if (on && !want) return { ...w, ...told, rewards: w.rewards.filter(x => x.rewardId !== t.id) }
    if (!on && want) return { ...w, ...told, rewards: [...w.rewards, toReward(t)] }
    return w
  }))
}
/** Publishing a campaign records it on the roue it allocated (mock of the 0011 join). */
export function markRoueUsed(roueId: string, campaign: string): void {
  setRoues(getRoues().map(w => w.id === roueId ? { ...w, usedBy: w.usedBy.includes(campaign) ? w.usedBy : [...w.usedBy, campaign], pending: false } : w))
}
export function setRoueScope(roueId: string, scope: RoueScope): void {
  setRoues(getRoues().map(w => w.id === roueId ? { ...w, scope, locations: scope === 'group' ? [] : w.locations } : w))
}
/** Where a roue plays, as the merchant reads it. */
export function placesLabel(roue: Roue, names: Record<string, string> = {}): string {
  if (roue.scope === 'group' || !roue.locations?.length) return 'Tous les établissements'
  return roue.locations.map(id => names[id] ?? id).join(' · ')
}
/** Weighted draw over the drawable types — the mock of the server-side draw (CJ-05). */
export function drawFromRoue(roue: Roue): RoueReward | null {
  const live = drawable(roue); const total = live.reduce((s, x) => s + x.weight, 0)
  if (!live.length || total <= 0) return null
  let r = Math.random() * total
  for (const x of live) { r -= x.weight; if (r <= 0) return x }
  return live[live.length - 1]
}

/** R2 — the express path creates a roue named after the campaign, visible in
 *  Mes roues and editable after. One type at 100 %: « Commencez avec une seule ». */
export function createExpressRoue(offer: string, value: string, second?: { offer: string; value: string }): Roue {
  const parse = (v: string) => parseFloat(v.replace(',', '.').replace(/[^\d.]/g, '')) || 0
  /* a gift the merchant already created under the same name is reused, never duplicated (D-47: Cadeaux first, then the express) */
  const existing = getLocalTypes()
  const mk = (o: string, v: string): GiftType => existing.find(t => t.name.trim().toLowerCase() === o.trim().toLowerCase())
    ?? ({ id: `${newTypeId()}-${Math.random().toString(36).slice(2, 5)}`, name: o.trim(), internal: o.trim(), cost: parse(v), validityDays: 60, cap: null, condition: '', status: 'ok', stock: null, icon: guessIcon(o), category: undefined })
  const types = [mk(offer, value), ...(second && second.offer.trim() ? [mk(second.offer, second.value)] : [])]
  types.forEach(t => upsertType(t)) // the types join the library too — nothing hidden (CA-13)
  /* D-34b (05/09): one gift → a direct reveal; two gifts → a real roue. Shares from cost: the cheaper carries 70 %. */
  let rewards: RoueReward[]
  if (types.length === 1) rewards = [toReward(types[0], TENTHS)]
  else {
    const [a, b] = types
    const cheaperFirst = (a.cost || 0) <= (b.cost || 0)
    rewards = cheaperFirst ? [toReward(a, 700), toReward(b, 300)] : [toReward(a, 300), toReward(b, 700)]
  }
  const roue: Roue = {
    id: newRoueId(), name: `Roue — ${offer.trim()}`, scope: 'location', locations: [homeLocationId()], usedBy: ['Campagne express'], createdVia: 'express',
    rewards,
  }
  setRoues([...getRoues(), roue])
  setActiveRoueId(roue.id)
  return roue
}

/* D-49 (05/09 night) · the guide's step 4: a first roue from the gifts of step 3, the shares recommended from cost */
export function composeRoueFromTypes(types: GiftType[], name: string, locations: string[]): Roue {
  const use = types.slice(0, ROUE_MAX_TYPES)
  const base: Roue = { id: newRoueId(), name, scope: 'location', locations, usedBy: [], rewards: use.map(t => toReward(t, use.length === 1 ? TENTHS : Math.floor(TENTHS / use.length))) }
  let roue = use.length > 1 ? recommendShares(base, new Set()) : base
  /* whole percentages for a first roue — the remainder goes to the cheapest (first) gift */
  if (use.length > 1) {
    const rounded = roue.rewards.map(r => ({ ...r, weight: Math.max(10, Math.round(r.weight / 10) * 10) }))
    const sum = rounded.reduce((a, r) => a + r.weight, 0)
    rounded[0] = { ...rounded[0], weight: rounded[0].weight + (TENTHS - sum) }
    roue = { ...roue, rewards: rounded }
  }
  setRoues([...getRoues(), roue]); setActiveRoueId(roue.id)
  return roue
}

/* D-51 (05/09 night) · a gift's category: the field, else the category of its icon (guessed from the name), else « Général » */
export function typeCategory(t: GiftType): GiftCategory {
  return (t.category ?? GIFT_ICONS.find(i => i.id === (t.icon ?? guessIcon(t.name)))?.cat ?? 'general') as GiftCategory
}
